import { useEffect, useState } from 'react';
import { Alert, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from '@tanstack/react-query';
import type {
  TppCardSummary,
  TppCardsPage,
  TppEventSummary,
} from '../../../services/transitPassPool.service';
import CollapsibleHelpSection from '../../common/CollapsibleHelpSection';
import LoadingSpinner from '../../common/LoadingSpinner';
import TppCardHistoryTable from './TppCardHistoryTable';
import TppCardListEmptyFiltered from './TppCardListEmptyFiltered';
import TppCardListFilterBar from './TppCardListFilterBar';
import TppCardListPagination from './TppCardListPagination';
import TppCardPoolList from './TppCardPoolList';
import TppCardPoolStats from './TppCardPoolStats';
import TppEmptyCardRegister from './TppEmptyCardRegister';
import TppNextActionBanners from './TppNextActionBanners';
import {
  isTppBorrowable,
  isTppChargeActionVisible,
  isTppNeedsChargeFirst,
} from './tppCardPredicates';
import { TPP_CARD_PAGE_SIZE } from './useTppCardList';

type Stats = {
  totalCards: number;
  checkedOutCards: number;
  availableCards: number;
  needsChargeCards: number;
};

/** 一覧タブが必要とする一覧状態（useTppCardList の戻り値） */
export type TppListTabModel = {
  showRegisterEmptyHint: boolean;
  showCardFilterBar: boolean;
  showCardSearch: boolean;
  showInactiveToggle: boolean;
  /** プールが1枚以下（選択バッジ／選択解除がノイズ） */
  singleCardPool: boolean;
  selected: TppCardSummary | null;
  otherCheckedOut: TppCardSummary[];
  otherCheckedOutTotal: number;
  availableCards: number | null;
  needsChargeCards: number | null;
  stats: Stats | null | undefined;
  cardsQuery: UseQueryResult<TppCardsPage, Error>;
  cardPage: number;
  setCardPage: (n: number) => void;
  cardSearch: string;
  cardSearchInput: string;
  setCardSearchInput: (s: string) => void;
  flushCardSearch: (next?: string) => void;
  showInactiveCards: boolean;
  setShowInactiveCards: (v: boolean) => void;
  checkedOutOnly: boolean;
  setCheckedOutOnly: (v: boolean | ((prev: boolean) => boolean)) => void;
  clearCardSelection: () => void;
  selectCard: (c: TppCardSummary) => void;
  focusAvailableCard: () => void;
  focusNeedsChargeCard: () => void;
};

export type TppListTabOps = {
  onReload: () => void;
  onRegister: () => void;
  onBorrow: (c: TppCardSummary) => void;
  onReturn: (c: TppCardSummary) => void;
  onCharge: (c: TppCardSummary) => void;
  onEditCard: (c: TppCardSummary) => void;
  onEditEvent: (ev: TppEventSummary) => void;
  setActiveTabSetup: () => void;
};

export type TppListTabProof = {
  onUploadProof: (eventId: number, file: File) => void;
  uploadProofPending: boolean;
  uploadProofEventId?: number;
  uploadProofError: boolean;
  onViewProof: (eventId: number) => void;
  proofViewerLoading: boolean;
};

type Props = {
  list: TppListTabModel;
  poolKey: string;
  money: (n: number) => string;
  amountPrefix: string;
  amountSuffix: string;
  formatAbsAmount: (n: number) => string;
  zeroBalanceLabel: string;
  ops: TppListTabOps;
  proof: TppListTabProof;
};

/** カード一覧タブ（統計・フィルタ・一覧・次アクション・履歴） */
export default function TppListTab({
  list,
  poolKey,
  money,
  amountPrefix,
  amountSuffix,
  formatAbsAmount,
  zeroBalanceLabel,
  ops,
  proof,
}: Props) {
  const { t } = useTranslation();
  const {
    showRegisterEmptyHint,
    showCardFilterBar,
    showCardSearch,
    showInactiveToggle,
    singleCardPool,
    selected,
    otherCheckedOut,
    otherCheckedOutTotal,
    availableCards,
    needsChargeCards,
    stats,
    cardsQuery,
    cardPage,
    setCardPage,
    cardSearch,
    cardSearchInput,
    setCardSearchInput,
    flushCardSearch,
    showInactiveCards,
    setShowInactiveCards,
    checkedOutOnly,
    setCheckedOutOnly,
    clearCardSelection,
    selectCard,
    focusAvailableCard,
    focusNeedsChargeCard,
  } = list;

  const [selectedHasHistory, setSelectedHasHistory] = useState(false);
  useEffect(() => {
    setSelectedHasHistory(false);
  }, [selected?.id]);

  const listedTotal = cardsQuery.data?.total ?? 0;
  const checkedOutCount = Number(stats?.checkedOutCards ?? 0);
  const hideOpsHelp =
    checkedOutCount > 0 ||
    (!!selected && (selected.isCheckedOut || isTppNeedsChargeFirst(selected)));
  /** 1枚＆無効トグル無しならソロ行にリロードを載せ、概況カードを出さない */
  const soloOwnsReload = singleCardPool && !showInactiveToggle;
  /** 無効トグルも無いのにリロードだけ残すと概況下が孤立アイコンになるので出さない */
  const showCompactExtras = !showCardSearch && !soloOwnsReload && showInactiveToggle;

  const clearFilters = () => {
    setCardSearchInput('');
    flushCardSearch('');
    setCheckedOutOnly(false);
    setShowInactiveCards(false);
  };

  return (
    <>
      {!showRegisterEmptyHint && listedTotal > 0 && !selectedHasHistory && !hideOpsHelp && (
        <CollapsibleHelpSection
          titleKey="tpp.help_title"
          descriptionKey="tpp.help_body"
          stepsKey={['tpp.help_step_borrow', 'tpp.help_step_return', 'tpp.help_step_history']}
          storageKey="transit_pass_pool"
          collapsedButtonKey="tpp.help_show_ops"
        />
      )}

      {stats != null && (
        <TppCardPoolStats
          totalCards={Number(stats.totalCards)}
          checkedOutCards={checkedOutCount}
          availableCards={availableCards}
          needsChargeCards={needsChargeCards}
          checkedOutOnly={checkedOutOnly}
          onToggleCheckedOutOnly={() => setCheckedOutOnly((v) => !v)}
          onFocusAvailable={focusAvailableCard}
          onFocusNeedsCharge={focusNeedsChargeCard}
          compactExtras={
            showCompactExtras
              ? {
                  showInactive: showInactiveCards,
                  onShowInactiveChange: setShowInactiveCards,
                  onReload: ops.onReload,
                  showInactiveToggle,
                }
              : undefined
          }
        />
      )}

      {showCardFilterBar && (
        <TppCardListFilterBar
          searchInput={cardSearchInput}
          onSearchInputChange={setCardSearchInput}
          onSearchApply={flushCardSearch}
          showInactive={showInactiveCards}
          onShowInactiveChange={setShowInactiveCards}
          showInactiveToggle={showInactiveToggle}
          checkedOutOnly={checkedOutOnly}
          onCheckedOutOnlyChange={setCheckedOutOnly}
          showCheckedOutCheckbox={!(stats != null && checkedOutCount > 0)}
          onReload={ops.onReload}
        />
      )}

      {cardsQuery.isError && <Alert variant="danger">{t('tpp.load_error')}</Alert>}

      {showRegisterEmptyHint ? (
        <TppEmptyCardRegister onRegister={ops.onRegister} onGotoSetup={ops.setActiveTabSetup} />
      ) : (
        <Card className="mb-4 border-0 shadow-sm overflow-hidden">
          <Card.Body className="p-0">
            {cardsQuery.isLoading ? (
              <div className="text-center py-5">
                <LoadingSpinner text={t('common.loading')} />
              </div>
            ) : (cardsQuery.data?.items ?? []).length === 0 ? (
              <TppCardListEmptyFiltered
                hasActiveFilters={
                  !!cardSearch ||
                  checkedOutOnly ||
                  showInactiveCards ||
                  cardSearchInput.trim() !== ''
                }
                onClearFilters={clearFilters}
                onRegister={ops.onRegister}
              />
            ) : (
              <TppCardPoolList
                items={cardsQuery.data?.items ?? []}
                selectedId={selected?.id}
                soloLayout={singleCardPool}
                onReload={soloOwnsReload ? ops.onReload : undefined}
                money={money}
                onSelect={selectCard}
                onBorrow={ops.onBorrow}
                onReturn={ops.onReturn}
                onCharge={ops.onCharge}
                onEdit={ops.onEditCard}
              />
            )}
          </Card.Body>
        </Card>
      )}

      {cardsQuery.data != null && (
        <TppCardListPagination
          page={cardPage}
          total={cardsQuery.data.total}
          pageSize={TPP_CARD_PAGE_SIZE}
          onPageChange={setCardPage}
        />
      )}

      {!selected && listedTotal > 0 && (
        <p className="small text-muted mb-3">
          {checkedOutCount > 0
            ? t('tpp.hint_select_card_checked_out')
            : t('tpp.hint_select_card')}
        </p>
      )}

      <TppNextActionBanners
        selected={selected}
        otherCheckedOut={otherCheckedOut}
        otherCheckedOutTotal={otherCheckedOutTotal}
        money={money}
        zeroBalanceLabel={zeroBalanceLabel}
        selectedHasHistory={selectedHasHistory}
        onReturn={ops.onReturn}
        onCharge={ops.onCharge}
      />

      {selected && (
        <div id="tpp-card-history">
          <TppCardHistoryTable
            cardId={selected.id}
            displayNumber={selected.displayNumber}
            soloCard={singleCardPool}
            poolKey={poolKey}
            amountPrefix={amountPrefix}
            amountSuffix={amountSuffix}
            formatAbsAmount={formatAbsAmount}
            emptyCanCheckout={isTppBorrowable(selected)}
            emptyCanCharge={isTppChargeActionVisible(selected)}
            emptyInactive={!selected.isActive}
            onEditEvent={ops.onEditEvent}
            onUploadProof={proof.onUploadProof}
            uploadProofPending={proof.uploadProofPending}
            uploadProofEventId={proof.uploadProofEventId}
            uploadProofError={proof.uploadProofError}
            onViewProof={proof.onViewProof}
            proofViewerLoading={proof.proofViewerLoading}
            onEventsTotalChange={(n) => setSelectedHasHistory(n > 0)}
            onClearSelection={singleCardPool ? undefined : clearCardSelection}
          />
        </div>
      )}
    </>
  );
}
